import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { CalendarDays, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatCentsUSD } from "@/lib/utils/currency.utils";
import {
  formatPayerMethodSummary,
  type PayerProfile,
} from "@/services/stripe-payer-profile.service";
import type { RecipientAutopayState } from "@/services/vendor-autopay.service";
import type { IndividualPaymentDue, SubsPaymentDue } from "../payment-dues.entity";
import { disableRecipientAutopay } from "../payment-dues.api";
import AutopayConsentDialog from "./AutopayConsentDialog";

type RecipientType = "individual" | "subs";

interface AutopaySectionProps {
  recipientType: RecipientType;
  due: IndividualPaymentDue | SubsPaymentDue;
  autopayState: RecipientAutopayState | null;
  autopayLoading: boolean;
  payerProfile: PayerProfile | null;
  onAutopayChange: (state: RecipientAutopayState) => void;
}

function lastRunBadge(state: RecipientAutopayState) {
  if (state.lastRunStatus === "succeeded") {
    return <Badge variant="default" className="bg-green-600 hover:bg-green-700 text-xs">Succeeded</Badge>;
  }
  if (state.lastRunStatus === "failed") {
    return <Badge variant="destructive" className="text-xs">Failed</Badge>;
  }
  return <Badge variant="secondary" className="text-xs">Skipped</Badge>;
}

export default function AutopaySection({
  recipientType,
  due,
  autopayState,
  autopayLoading,
  payerProfile,
  onAutopayChange,
}: AutopaySectionProps) {
  const [consentDialogOpen, setConsentDialogOpen] = useState(false);
  const [confirmDisableOpen, setConfirmDisableOpen] = useState(false);
  const [disabling, setDisabling] = useState(false);

  const enabled = Boolean(autopayState?.enabled);
  const hasSavedMethod = payerProfile?.status === "active" && Boolean(payerProfile.last4);
  const switchDisabled = autopayLoading || disabling || (!enabled && (!hasSavedMethod || !due.ready_for_payouts));

  const helperText = !hasSavedMethod
    ? "Save a payment method in the Payer Profile section at the top of this page to enable autopay."
    : !due.ready_for_payouts
      ? `This ${recipientType === "individual" ? "individual" : "subcontractor"} is not ready for payouts yet.`
      : enabled
        ? `Charged daily for dues past Net ${due.payment_terms_days} terms, one combined charge per day.`
        : "Automatically pay dues once they pass the agreed Net terms.";

  const handleToggle = (next: boolean) => {
    // The switch is controlled by server state only; the dialogs drive the
    // actual transition so a failed call never leaves the toggle lying.
    if (next) {
      setConsentDialogOpen(true);
    } else {
      setConfirmDisableOpen(true);
    }
  };

  const handleDisable = async () => {
    setConfirmDisableOpen(false);
    setDisabling(true);
    try {
      const state = await disableRecipientAutopay(recipientType, due.id);
      onAutopayChange(state);
      toast.success(`Autopay turned off for ${due.name}.`);
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to turn off autopay.");
    } finally {
      setDisabling(false);
    }
  };

  return (
    <div className="rounded-lg border p-3 space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium">Autopay</h4>
          {autopayState?.paused && (
            <Badge variant="destructive" className="text-xs">Paused</Badge>
          )}
          {(autopayLoading || disabling) && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          )}
        </div>
        <Switch
          checked={enabled}
          disabled={switchDisabled}
          aria-label={`Toggle autopay for ${due.name}`}
          data-testid={`autopay-switch-${recipientType}-${due.id}`}
          onCheckedChange={handleToggle}
        />
      </div>
      <p className="text-xs text-muted-foreground">{helperText}</p>
      {autopayState?.paused && autopayState.pauseReason && (
        <p className="text-xs text-destructive">
          {autopayState.pauseReason} Turn autopay back on to resume after fixing your payment method.
        </p>
      )}

      {enabled && autopayState?.lastRunAt && (
        <div className="space-y-1.5 border-t pt-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Last run</span>
            <span className="text-xs font-medium flex items-center gap-1">
              <CalendarDays className="h-3 w-3" />
              {new Date(autopayState.lastRunAt).toLocaleDateString()}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Result</span>
            {lastRunBadge(autopayState)}
          </div>
          {autopayState.lastRunAmountCents != null && autopayState.lastRunAmountCents > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Amount</span>
              <span className="text-xs font-medium">{formatCentsUSD(autopayState.lastRunAmountCents)}</span>
            </div>
          )}
          {autopayState.lastRunStatus === "failed" && autopayState.lastRunErrorMessage && (
            <p className="text-xs text-destructive">{autopayState.lastRunErrorMessage}</p>
          )}
        </div>
      )}

      <AutopayConsentDialog
        open={consentDialogOpen}
        onOpenChange={setConsentDialogOpen}
        recipientType={recipientType}
        recipientId={due.id}
        recipientName={due.name}
        paymentTermsDays={due.payment_terms_days}
        payerMethodSummary={formatPayerMethodSummary(payerProfile)}
        onEnabled={onAutopayChange}
      />

      <AlertDialog open={confirmDisableOpen} onOpenChange={setConfirmDisableOpen}>
        <AlertDialogContent className="z-[70]">
          <AlertDialogHeader>
            <AlertDialogTitle>Turn off autopay for {due.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Past-term dues will no longer be charged automatically. You can still pay manually from this page.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleDisable()}>Turn off</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
